// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {useForm} from 'react-hook-form';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import Collapsible from '../../../../elements/Collapsible/Collapsible';

import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomExitPrompt from '../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {Route, Switch} from 'react-router-dom';
import {colors} from '../../../../assets/styles/colors';
import {usePage} from '../../../page/utils';
import NewFeatureBadge from '../../../../elements/NewFeatureBadge/NewFeatureBadge';
import analytics from '../../../../analytics';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useStoreCountry} from '../../../country/utils';
import {igFbShopAvailableCountries} from './FacebookIntegration';

import type {SocialMedia, SocialMediaType} from '../../../../entities';
import type {SaveSocialMediaRequest} from '../api';
import Toast from '../../../../elements/Toast';

type Props = {|
  isFetchingSocial?: boolean,
  socialMedias?: SocialMedia[],
  socialMediaTypes?: SocialMediaType[],
  saveSocialMedias: ({socialMediasData: SaveSocialMediaRequest[]}) => void,
  isSaving?: boolean,
  savingSuccess?: boolean,
  error?: string,
  resetState: () => void,
|};

const useStyles = makeStyles(theme => ({
  subTitle: {
    marginBottom: theme.spacing(2),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  logoImg: {
    width: 20,
    height: 20,
    opacity: 0.6,
    marginTop: '14px',
    marginRight: theme.spacing(2),
  },
  facebookButton: {
    marginTop: theme.spacing(3),
    width: '100%',
    minHeight: 56,
    background: 'linear-gradient(180deg, #1778F2 0%, #0A6CE7 100%)',
    borderRadius: '12px',
    color: 'white',
    whiteSpace: 'pre-wrap',
    justifyContent: 'flex-start',
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
  copyFbLinkBtn: {
    borderRadius: '8px',
  },
  divider: {
    margin: theme.spacing(5, 0),
    background: colors.grey200,
    width: '100%',
  },
}));

export default usePage<Props>(SocialMedias, 'Social Media Configuration');

function SocialMedias(props: Props) {
  const {
    isFetchingSocial,
    socialMedias = [],
    socialMediaTypes = [],
    saveSocialMedias,
    isSaving,
    savingSuccess,
    error,
    resetState,
  } = props;
  const classes = useStyles();
  const {handleSubmit, register, formState, errors, reset} = useForm();
  const {isDirty} = formState;

  const hasStoreSocialMediaFeature = useIsFeatureInTier('STORE_SOCIAL_MEDIA');

  const country = useStoreCountry();
  const igFbShopAvailable = igFbShopAvailableCountries.includes(country?.code);

  const [noChangesToast, setNoChangesToast] = useState(false);

  useEffect(() => () => resetState(), [resetState]);

  const getDefaultValue = (socialMediaTypeId: string) => {
    const c = socialMedias.find(
      t => t.socialMediaType.id === socialMediaTypeId
    );
    if (c) return c.identifier;
  };

  const onSubmit = data => {
    const socialMediasData: SaveSocialMediaRequest[] = Object.entries(data)
      .filter(([key, value]) => {
        const isEmpty = value === undefined || value === null;

        if (isEmpty) return false;

        const savedSocialMedia = socialMedias.find(
          socialMedia => socialMedia.socialMediaType.name === key
        );

        const noChanges =
          savedSocialMedia && savedSocialMedia.identifier === value;

        return !noChanges;
      })
      .map(([key, value]) => {
        const socialMediaType = socialMediaTypes.find(
          socialMedia => socialMedia.name === key
        );

        // $FlowFixMe
        if (!socialMediaType) return false;

        return {
          // $FlowFixMe
          identifier: value,
          socialMediaTypeId: socialMediaType.id,
        };
      })
      .filter(Boolean);

    if (socialMediasData.length) {
      saveSocialMedias({socialMediasData});
      reset(data);
    } else {
      setNoChangesToast(true);
    }
  };

  useEffect(() => {
    if (savingSuccess) {
      analytics.fireCustomEvent('Social Media Configured', undefined, [
        'user-guiding',
      ]);
    }
  }, [savingSuccess]);

  return (
    <>
      <CustomExitPrompt when={isDirty} />
      <SnackBar mode="error" message={error} />
      <SnackBar
        mode="success"
        message={savingSuccess ? 'Se guardaron los cambios' : undefined}
      />

      <Toast
        color="warning"
        severity="warning"
        open={noChangesToast}
        onClose={() => setNoChangesToast(false)}
      >
        No hay cambios para guardar
      </Toast>
      <Switch>
        <Route exact path="/my-store/social-medias">
          <Collapsible title="Redes sociales">
            <Typography variant="body1" className={classes.subTitle}>
              Vincula tus redes sociales y aparacerán en tu tienda virtual.
            </Typography>
            <Grid container spacing={3} alignItems="flex-start">
              {socialMediaTypes.map(smt => (
                <Grid key={smt.id} item xs={12} sm={6}>
                  <CustomTextField
                    label={smt.name}
                    fullWidth
                    name={smt.name}
                    autoComplete={smt.name}
                    error={!!errors[smt.name]}
                    helperText={
                      errors[smt.name] ? errors[smt.name].message : ''
                    }
                    inputRef={register()}
                    disabled={
                      isSaving ||
                      isFetchingSocial ||
                      !hasStoreSocialMediaFeature
                    }
                    InputProps={{
                      startAdornment: (
                        <img
                          src={`data:image/svg+xml;utf8,${smt.logo}`}
                          className={classes.logoImg}
                          alt={smt.name}
                        />
                      ),
                      style: {borderRadius: '8px'},
                    }}
                    defaultValue={getDefaultValue(smt.id)}
                  />
                </Grid>
              ))}
              {hasStoreSocialMediaFeature && (
                <Grid item xs={12}>
                  <CustomButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isSaving}
                    onClick={handleSubmit(onSubmit)}
                    className={classes.saveButton}
                  >
                    Guardar cambios
                  </CustomButton>
                </Grid>
              )}
            </Grid>
          </Collapsible>
          {igFbShopAvailable && (
            <CustomButton
              variant="contained"
              className={classes.facebookButton}
              to="/integrations/facebook-integration"
            >
              Conecta tu tienda con Facebook Shop e Instagram
              <NewFeatureBadge />
            </CustomButton>
          )}
        </Route>
      </Switch>
    </>
  );
}

SocialMedias.propTypes = {
  isFetchingSocial: PropTypes.bool,
  socialMedias: PropTypes.array,
  socialMediaTypes: PropTypes.array,
  saveSocialMedias: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  savingSuccess: PropTypes.bool,
  error: PropTypes.string,
  resetState: PropTypes.func.isRequired,
};
